/**
 * @file PostgresqlConnections.js
 * WHAT: Full CRUD management interface for PostgreSQL database connections with configurable SSL modes and multiple authentication methods.
 * WHY: PostgreSQL connections require technology-specific parameters (ssl_mode with 6 levels, search_path, SCRAM-SHA-256 auth, application_name) that differ from other database technologies.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../services/api';
import { dedupeMappingsByIdentifier } from '../utils/mappingUtils';
import { SourceTimezoneSelect } from './common';
import 'bootstrap/dist/css/bootstrap.min.css';

/**
 * WHAT: Renders the PostgreSQL connection management page with list/create/edit/view/delete/test operations and filtering by SSL mode, status, and active state.
 * WHY: PostgreSQL DBAs need to register connections with granular SSL mode control (disable through verify-full) and schema search paths so DAMA can collect user access data for compliance reviews.
 */
const PostgresqlConnections = ({ embedded = false, prefill = null, onCreated, onCancel } = {}) => {
  // State management
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testingId, setTestingId] = useState(null);

  // Test result modal state
  const [showTestResultModal, setShowTestResultModal] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // PostgreSQL databases from app_database_mapping (for dropdown)
  const [postgresqlDatabases, setPostgresqlDatabases] = useState([]);
  const [loadingDatabases, setLoadingDatabases] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    ssl_mode: '',
    connection_status: '',
    is_active: ''
  });

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [currentConnection, setCurrentConnection] = useState(null);

  // Form data
  const initialFormData = {
    connection_name: '',
    database_identifier: '',
    host: '',
    port: 5432,
    database_name: '',
    username: '',
    vault_path: '',
    auth_method: 'PASSWORD',
    ssl_mode: 'prefer',
    ssl_root_cert: '',
    ssl_cert: '',
    ssl_key: '',
    pool_min: 1,
    pool_max: 5,
    connection_timeout: 30,
    application_name: 'DAMA',
    search_path: '',
    source_timezone: 'UTC',
    is_active: true
  };
  const [formData, setFormData] = useState(initialFormData);

  // Fetch connections
  const fetchConnections = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (filters.connection_status) params.connection_status = filters.connection_status;
      if (filters.is_active !== '') params.is_active = filters.is_active;

      const response = await api.databases.getPostgresqlSourceConnections(params);

      if (response.data.success) {
        setConnections(response.data.connections);
      } else {
        setError('Failed to fetch PostgreSQL connections');
      }
    } catch (err) {
      setError(`Error fetching connections: ${err.message}`);
      console.error('Error fetching PostgreSQL connections:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnections();
  }, [filters.connection_status, filters.is_active]);

  // Fetch PostgreSQL databases from app_database_mapping
  const fetchPostgresqlDatabases = async () => {
    try {
      setLoadingDatabases(true);
      const response = await api.databases.getAppDatabaseMappings();

      const mappings = Array.isArray(response.data) ? response.data : [];

      const databases = dedupeMappingsByIdentifier(mappings
        .filter(m => m.database_type?.toUpperCase() === 'POSTGRESQL' && m.is_active !== false))
        .map(m => ({
          database_identifier: m.database_identifier,
          database_name: m.database_name,
          display_name: m.display_name || m.database_name,
          app_id: m.app_id,
          database_host: m.database_host,
          database_environment: m.database_environment
        }))
        .sort((a, b) => a.display_name.localeCompare(b.display_name));

      setPostgresqlDatabases(databases);
    } catch (err) {
      console.error('Error fetching PostgreSQL databases:', err);
    } finally {
      setLoadingDatabases(false);
    }
  };

  useEffect(() => {
    fetchPostgresqlDatabases();
  }, []);

  // EMBED MODE: when hosted inside another screen (e.g. the Compliance Matrix
  // "create a connection for the database you just added" popup), auto-open the
  // create modal with the database identifier prefilled and locked. Driven once
  // on mount; the host controls visibility by mounting/unmounting this component.
  useEffect(() => {
    if (embedded) {
      setModalMode('create');
      setCurrentConnection(null);
      setFormData({
        ...initialFormData,
        ...(prefill || {}),
        connection_name: (prefill && prefill.connection_name) ||
          (prefill && prefill.database_identifier ? `${prefill.database_identifier}_CONN` : ''),
      });
      setShowModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded]);

  // Filtered connections
  const filteredConnections = useMemo(() => {
    let result = connections;

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(conn =>
        conn.connection_name?.toLowerCase().includes(searchLower) ||
        conn.host?.toLowerCase().includes(searchLower) ||
        conn.database_name?.toLowerCase().includes(searchLower) ||
        conn.username?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.ssl_mode) {
      result = result.filter(conn => conn.ssl_mode === filters.ssl_mode);
    }

    return result;
  }, [connections, filters.search, filters.ssl_mode]);

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      search: '',
      ssl_mode: '',
      connection_status: '',
      is_active: ''
    });
  };

  // Modal operations
  const openCreateModal = () => {
    setModalMode('create');
    setFormData(initialFormData);
    setCurrentConnection(null);
    setShowModal(true);
  };


  const openEditModal = async (connectionId) => {
    try {
      const response = await api.databases.getPostgresqlSourceConnectionById(connectionId);
      if (response.data.success) {
        setModalMode('edit');
        setCurrentConnection(response.data.connection);
        setFormData({
          ...response.data.connection,
          is_active: response.data.connection.is_active ?? true
        });
        setShowModal(true);
      }
    } catch (err) {
      alert(`Error loading connection: ${err.message}`);
    }
  };

  const openViewModal = async (connectionId) => {
    try {
      const response = await api.databases.getPostgresqlSourceConnectionById(connectionId);
      if (response.data.success) {
        setModalMode('view');
        setCurrentConnection(response.data.connection);
        setFormData(response.data.connection);
        setShowModal(true);
      }
    } catch (err) {
      alert(`Error loading connection: ${err.message}`);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentConnection(null);
    // In embed mode, closing the modal means the host should tear down the popup.
    if (embedded && onCancel) onCancel();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // CRUD operations
  const handleCreate = async () => {
    try {
      if (!formData.connection_name || !formData.host || !formData.database_name || !formData.username) {
        alert('Please fill in required fields: Connection Name, Host, Database Name, and Username');
        return;
      }

      const response = await api.databases.createPostgresqlSourceConnection(formData);

      if (response.data.success) {
        alert('PostgreSQL connection created successfully!');
        if (embedded && onCreated) {
          // Host (e.g. Compliance Matrix) owns what happens next (close popup + refresh).
          onCreated(response.data);
        } else {
          closeModal();
          fetchConnections();
        }
      } else {
        alert(`Error: ${response.data.error}`);
      }
    } catch (err) {
      alert(`Error creating connection: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleUpdate = async () => {
    try {
      const response = await api.databases.updatePostgresqlSourceConnection(
        currentConnection.id,
        formData
      );

      if (response.data.success) {
        alert('PostgreSQL connection updated successfully!');
        closeModal();
        fetchConnections();
      } else {
        alert(`Error: ${response.data.error}`);
      }
    } catch (err) {
      alert(`Error updating connection: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleDelete = async (connectionId) => {
    if (!window.confirm('Are you sure you want to delete this connection?')) {
      return;
    }

    try {
      const response = await api.databases.deletePostgresqlSourceConnection(connectionId);

      if (response.data.success) {
        alert('Connection deleted successfully!');
        fetchConnections();
      } else {
        alert(`Error: ${response.data.error}`);
      }
    } catch (err) {
      alert(`Error deleting connection: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleTest = async (connectionId) => {
    const conn = connections.find(c => c.id === connectionId);
    setTestingId(connectionId);
    try {
      const response = await api.databases.testPostgresqlSourceConnection(connectionId);

      setTestResult({
        connectionName: conn?.connection_name || 'Unknown',
        databaseType: 'PostgreSQL',
        success: response.data.success,
        status: response.data.status || (response.data.success ? 'SUCCESS' : 'FAILED'),
        message: response.data.message,
        responseTime: response.data.response_time,
        databaseVersion: response.data.database_version,
        error: response.data.error
      });
      setShowTestResultModal(true);
      fetchConnections();
    } catch (err) {
      setTestResult({
        connectionName: conn?.connection_name || 'Unknown',
        databaseType: 'PostgreSQL',
        success: false,
        status: 'FAILED',
        message: null,
        error: err.response?.data?.message || err.response?.data?.error || err.message
      });
      setShowTestResultModal(true);
      fetchConnections();
    } finally {
      setTestingId(null);
    }
  };

  // Badge colors
  const getStatusBadgeColor = (status) => {
    const colors = {
      'SUCCESS': 'success',
      'FAILED': 'danger',
      'PENDING': 'warning'
    };
    return colors[status] || 'secondary';
  };

  const getSslModeBadgeColor = (mode) => {
    const colors = {
      'disable': 'danger',
      'allow': 'warning',
      'prefer': 'info',
      'require': 'primary',
      'verify-ca': 'success',
      'verify-full': 'success'
    };
    return colors[mode] || 'secondary';
  };

  const getAuthBadgeColor = (method) => {
    const colors = {
      'PASSWORD': 'primary',
      'MD5': 'info',
      'SCRAM': 'success',
      'CERT': 'warning',
      'GSSAPI': 'dark'
    };
    return colors[method] || 'secondary';
  };

  return (
    <div className={embedded ? '' : 'container-fluid mt-4'}>
      {!embedded && (<>
      <div className="row mb-4">
        <div className="col-12 d-flex justify-content-between align-items-center">
          <div>
            <h2>
              <i className="fas fa-database me-2" style={{ color: '#336791' }}></i>
              PostgreSQL Connections
            </h2>
            <p className="text-muted">Manage PostgreSQL database connections (source_postgresql_connections)</p>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary" onClick={fetchConnections} title="Refresh">
              <i className="fas fa-sync-alt me-1"></i>
              Refresh
            </button>
            <button className="btn btn-primary" onClick={openCreateModal}>
              <i className="fas fa-plus me-1"></i>
              Add Connection
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body py-2">
          <div className="row g-2 align-items-center">
            <div className="col-md-3">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search connections..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={filters.ssl_mode}
                onChange={(e) => handleFilterChange('ssl_mode', e.target.value)}
              >
                <option value="">All SSL Modes</option>
                <option value="disable">Disable</option>
                <option value="allow">Allow</option>
                <option value="prefer">Prefer</option>
                <option value="require">Require</option>
                <option value="verify-ca">Verify CA</option>
                <option value="verify-full">Verify Full</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={filters.connection_status}
                onChange={(e) => handleFilterChange('connection_status', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="SUCCESS">Success</option>
                <option value="FAILED">Failed</option>
                <option value="PENDING">Pending</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={filters.is_active}
                onChange={(e) => handleFilterChange('is_active', e.target.value)}
              >
                <option value="">All Active Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div className="col-md-2">
              {(filters.search || filters.ssl_mode || filters.connection_status || filters.is_active !== '') && (
                <button className="btn btn-outline-secondary btn-sm" onClick={clearFilters}>
                  <i className="fas fa-times me-1"></i>
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Connections Table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading PostgreSQL connections...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      ) : filteredConnections.length === 0 ? (
        <div className="alert alert-info" role="alert">
          <i className="fas fa-info-circle me-2"></i>
          {connections.length === 0
            ? 'No PostgreSQL connections found. Click "Add Connection" to create one.'
            : 'No connections match your search criteria.'}
        </div>
      ) : (
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover table-bordered">
                <thead className="table-dark">
                  <tr>
                    <th>Connection Name</th>
                    <th>Server</th>
                    <th>Database</th>
                    <th>Username</th>
                    <th>Auth</th>
                    <th>SSL Mode</th>
                    <th>Test Status</th>
                    <th>Active</th>
                    <th style={{ width: '140px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredConnections.map((conn) => (
                    <tr key={conn.id}>
                      <td>
                        <strong>{conn.connection_name}</strong>
                        {conn.database_identifier && (
                          <div className="text-muted small">{conn.database_identifier}</div>
                        )}
                      </td>
                      <td>
                        <code className="small">{conn.connection_string_display}</code>
                      </td>
                      <td>{conn.database_name}</td>
                      <td>{conn.username}</td>
                      <td>
                        <span className={`badge bg-${getAuthBadgeColor(conn.auth_method)}`}>
                          {conn.auth_method}
                        </span>
                      </td>
                      <td>
                        <span className={`badge bg-${getSslModeBadgeColor(conn.ssl_mode)}`}>
                          {conn.ssl_mode}
                        </span>
                      </td>
                      <td>
                        <span className={`badge bg-${getStatusBadgeColor(conn.connection_status)}`}>
                          {conn.connection_status || 'NOT_TESTED'}
                        </span>
                        {conn.last_test_at && (
                          <div className="text-muted small mt-1">
                            {new Date(conn.last_test_at).toLocaleString()}
                          </div>
                        )}
                      </td>
                      <td>
                        {conn.is_active ? (
                          <span className="badge bg-success">Active</span>
                        ) : (
                          <span className="badge bg-secondary">Inactive</span>
                        )}
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm" role="group">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => openViewModal(conn.id)}
                            title="View Details"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            className="btn btn-outline-warning"
                            onClick={() => openEditModal(conn.id)}
                            title="Edit"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-outline-info"
                            onClick={() => handleTest(conn.id)}
                            title="Test Connection"
                            disabled={testingId === conn.id}
                          >
                            {testingId === conn.id ? (
                              <span className="spinner-border spinner-border-sm"></span>
                            ) : (
                              <i className="fas fa-plug"></i>
                            )}
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(conn.id)}
                            title="Delete"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3">
              <small className="text-muted">
                Showing {filteredConnections.length} of {connections.length} connection(s)
              </small>
            </div>
          </div>
        </div>
      )}
      </>)}

      {/* Modal for Create/Edit/View */}
      {showModal && (
        <div className="modal fade show" style={{
          display: 'block',
          backgroundColor: 'rgba(0,0,0,0.5)',
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 1050,
          overflow: 'auto'
        }}>
          <div style={{ padding: '20px' }}>
            <div className="modal-content" style={{ maxWidth: '1400px', width: '100%', margin: '0 auto' }}>
              <div className="modal-header" style={{ backgroundColor: '#336791', color: 'white' }}>
                <h5 className="modal-title">
                  {modalMode === 'create' && <><i className="fas fa-plus me-2"></i>Add PostgreSQL Connection</>}
                  {modalMode === 'edit' && <><i className="fas fa-edit me-2"></i>Edit PostgreSQL Connection</>}
                  {modalMode === 'view' && <><i className="fas fa-eye me-2"></i>PostgreSQL Connection Details</>}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={closeModal}></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <form>
                  {/* Basic Information */}
                  <h6 className="border-bottom pb-2 mb-3">Basic Information</h6>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Connection Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.connection_name || ''}
                        onChange={(e) => handleInputChange('connection_name', e.target.value)}
                        disabled={modalMode === 'view'}
                        placeholder="e.g., HRPROD_POSTGRES"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Database Identifier <span className="text-danger">*</span></label>
                      <select
                        className="form-select"
                        value={formData.database_identifier || ''}
                        onChange={(e) => handleInputChange('database_identifier', e.target.value)}
                        disabled={modalMode === 'view' || loadingDatabases || (embedded && !!(prefill && prefill.database_identifier))}
                        required
                      >
                        <option value="">-- Select Database Identifier --</option>
                        {postgresqlDatabases.map(db => (
                          <option key={db.database_identifier} value={db.database_identifier}>
                            {db.database_identifier} - {db.display_name}
                            {db.database_environment ? ` (${db.database_environment})` : ''}
                          </option>
                        ))}
                      </select>
                      <small className="text-muted">
                        {loadingDatabases ? 'Loading database identifiers...' :
                         postgresqlDatabases.length === 0 ?
                           'No PostgreSQL databases found. Add databases in App Database Mapping first.' :
                           'Select the database identifier from App Database Mapping'}
                      </small>
                    </div>
                  </div>

                  {/* Connection Details */}
                  <h6 className="border-bottom pb-2 mb-3 mt-4">Connection Details</h6>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Host <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.host || ''}
                        onChange={(e) => handleInputChange('host', e.target.value)}
                        disabled={modalMode === 'view'}
                        placeholder="postgres.company.com or IP"
                      />
                    </div>
                    <div className="col-md-2 mb-3">
                      <label className="form-label">Port</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.port || 5432}
                        onChange={(e) => handleInputChange('port', parseInt(e.target.value))}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Database Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.database_name || ''}
                        onChange={(e) => handleInputChange('database_name', e.target.value)}
                        disabled={modalMode === 'view'}
                        placeholder="e.g., hrdb"
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Application Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.application_name || 'DAMA'}
                        onChange={(e) => handleInputChange('application_name', e.target.value)}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Search Path</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.search_path || ''}
                        onChange={(e) => handleInputChange('search_path', e.target.value)}
                        disabled={modalMode === 'view'}
                        placeholder="e.g., public, hr_schema"
                      />
                      <small className="text-muted">Schema search path (comma-separated)</small>
                    </div>
                  </div>

                  {/* Authentication */}
                  <h6 className="border-bottom pb-2 mb-3 mt-4">Authentication</h6>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Authentication Method</label>
                      <select
                        className="form-select"
                        value={formData.auth_method || 'PASSWORD'}
                        onChange={(e) => handleInputChange('auth_method', e.target.value)}
                        disabled={modalMode === 'view'}
                      >
                        <option value="PASSWORD">Password</option>
                        <option value="MD5">MD5</option>
                        <option value="SCRAM">SCRAM-SHA-256</option>
                        <option value="CERT">Certificate</option>
                        <option value="GSSAPI">GSSAPI/Kerberos</option>
                      </select>
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Username <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.username || ''}
                        onChange={(e) => handleInputChange('username', e.target.value)}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Vault Path</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.vault_path || ''}
                        onChange={(e) => handleInputChange('vault_path', e.target.value)}
                        disabled={modalMode === 'view'}
                        placeholder="dama/postgresql/hrprod"
                      />
                      <small className="text-muted">Path to the secret in HashiCorp Vault</small>
                    </div>
                  </div>

                  {/* SSL/TLS Settings */}
                  <h6 className="border-bottom pb-2 mb-3 mt-4">SSL/TLS Settings</h6>
                  <div className="row">
                    <div className="col-md-3 mb-3">
                      <label className="form-label">SSL Mode</label>
                      <select
                        className="form-select"
                        value={formData.ssl_mode || 'prefer'}
                        onChange={(e) => handleInputChange('ssl_mode', e.target.value)}
                        disabled={modalMode === 'view'}
                      >
                        <option value="disable">Disable</option>
                        <option value="allow">Allow</option>
                        <option value="prefer">Prefer (Default)</option>
                        <option value="require">Require</option>
                        <option value="verify-ca">Verify CA</option>
                        <option value="verify-full">Verify Full</option>
                      </select>
                      <small className="text-muted">Recommended: require or higher for production</small>
                    </div>
                    {['require', 'verify-ca', 'verify-full'].includes(formData.ssl_mode) && (
                      <>
                        <div className="col-md-3 mb-3">
                          <label className="form-label">Root Certificate</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.ssl_root_cert || ''}
                            onChange={(e) => handleInputChange('ssl_root_cert', e.target.value)}
                            disabled={modalMode === 'view'}
                            placeholder="/path/to/root.crt"
                          />
                        </div>
                        <div className="col-md-3 mb-3">
                          <label className="form-label">Client Certificate</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.ssl_cert || ''}
                            onChange={(e) => handleInputChange('ssl_cert', e.target.value)}
                            disabled={modalMode === 'view'}
                            placeholder="/path/to/client.crt"
                          />
                        </div>
                        <div className="col-md-3 mb-3">
                          <label className="form-label">Client Key</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.ssl_key || ''}
                            onChange={(e) => handleInputChange('ssl_key', e.target.value)}
                            disabled={modalMode === 'view'}
                            placeholder="/path/to/client.key"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* Connection Pool Settings */}
                  <h6 className="border-bottom pb-2 mb-3 mt-4">Connection Pool</h6>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Pool Min</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.pool_min || 1}
                        onChange={(e) => handleInputChange('pool_min', parseInt(e.target.value))}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Pool Max</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.pool_max || 5}
                        onChange={(e) => handleInputChange('pool_max', parseInt(e.target.value))}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Connection Timeout (seconds)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.connection_timeout || 30}
                        onChange={(e) => handleInputChange('connection_timeout', parseInt(e.target.value))}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                  </div>

                  {/* Status */}
                  <h6 className="border-bottom pb-2 mb-3 mt-4">Status</h6>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Active</label>
                      <select
                        className="form-select"
                        value={formData.is_active ? 'true' : 'false'}
                        onChange={(e) => handleInputChange('is_active', e.target.value === 'true')}
                        disabled={modalMode === 'view'}
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <SourceTimezoneSelect
                        value={formData.source_timezone}
                        onChange={(v) => handleInputChange('source_timezone', v)}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                  </div>

                  {/* View mode - show test status */}
                  {modalMode === 'view' && currentConnection && (
                    <>
                      <h6 className="border-bottom pb-2 mb-3 mt-4">Connection Test Results</h6>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Test Status</label>
                          <div>
                            <span className={`badge bg-${getStatusBadgeColor(currentConnection.connection_status)}`}>
                              {currentConnection.connection_status || 'NOT_TESTED'}
                            </span>
                          </div>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Last Tested</label>
                          <div>
                            {currentConnection.last_test_at
                              ? new Date(currentConnection.last_test_at).toLocaleString()
                              : 'Never'}
                          </div>
                        </div>
                        {currentConnection.last_test_error && (
                          <div className="col-md-12 mb-3">
                            <label className="form-label">Error Message</label>
                            <div className="alert alert-danger">{currentConnection.last_test_error}</div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </form>
              </div>
              <div className="modal-footer">
                {modalMode === 'view' ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-info"
                      onClick={() => handleTest(currentConnection.id)}
                    >
                      <i className="fas fa-plug me-1"></i>
                      Test Connection
                    </button>
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={() => setModalMode('edit')}
                    >
                      <i className="fas fa-edit me-1"></i>
                      Edit
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={closeModal}>
                      Close
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="btn btn-secondary" onClick={closeModal}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={modalMode === 'create' ? handleCreate : handleUpdate}
                    >
                      <i className={`fas fa-${modalMode === 'create' ? 'plus' : 'save'} me-1`}></i>
                      {modalMode === 'create' ? 'Create Connection' : 'Save Changes'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Result Modal */}
      {showTestResultModal && testResult && (
        <div className="modal fade show" style={{
          display: 'block',
          backgroundColor: 'rgba(0,0,0,0.5)',
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 1060,
          overflow: 'auto'
        }}>
          <div style={{ padding: '20px' }}>
            <div className="modal-content" style={{ maxWidth: '600px', width: '100%', margin: '0 auto' }}>
              <div className="modal-header" style={{
                backgroundColor: testResult.success ? '#28a745' : '#dc3545',
                color: 'white'
              }}>
                <h5 className="modal-title">
                  <i className={`fas ${testResult.success ? 'fa-check-circle' : 'fa-times-circle'} me-2`}></i>
                  Connection Test Result
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowTestResultModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="text-center mb-4">
                  <i className={`fas ${testResult.success ? 'fa-check-circle text-success' : 'fa-times-circle text-danger'}`} style={{ fontSize: '4rem' }}></i>
                  <h4 className={`mt-3 ${testResult.success ? 'text-success' : 'text-danger'}`}>
                    {testResult.success ? 'Connection Successful' : 'Connection Failed'}
                  </h4>
                </div>

                <div className="card mb-3">
                  <div className="card-body">
                    <table className="table table-sm mb-0">
                      <tbody>
                        <tr>
                          <td className="fw-bold" style={{ width: '40%' }}>Connection Name</td>
                          <td>{testResult.connectionName}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Database Type</td>
                          <td><span className="badge" style={{ backgroundColor: '#336791' }}>{testResult.databaseType}</span></td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Status</td>
                          <td>
                            <span className={`badge ${testResult.success ? 'bg-success' : 'bg-danger'}`}>
                              {testResult.status}
                            </span>
                          </td>
                        </tr>
                        {testResult.responseTime && (
                          <tr>
                            <td className="fw-bold">Response Time</td>
                            <td>{testResult.responseTime}</td>
                          </tr>
                        )}
                        {testResult.databaseVersion && (
                          <tr>
                            <td className="fw-bold">Database Version</td>
                            <td>{testResult.databaseVersion}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {testResult.message && (
                  <div className={`alert ${testResult.success ? 'alert-success' : 'alert-info'}`}>
                    <i className="fas fa-info-circle me-2"></i>
                    {testResult.message}
                  </div>
                )}

                {testResult.error && (
                  <div className="alert alert-danger">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <strong>Error:</strong> {testResult.error}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowTestResultModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostgresqlConnections;
